// src/contexts/LocationContext.jsx
import React, { createContext, useState, useEffect } from 'react';
import { getStorageItem, setStorageItem } from '../utils/storage';
import ApiService from '../services/ApiService';

export const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationSkipped, setLocationSkipped] = useState(false);
  const [distributorId, setDistributorId] = useState(null);
  const [nearestDistributor, setNearestDistributor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backgroundUpdate, setBackgroundUpdate] = useState(false);
  
  // Nouveaux états pour la gestion du lieu de livraison
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  
  // ✅ NOUVEAU : Callback pour notifier les changements de distributeur
  const [distributorChangeCallback, setDistributorChangeCallback] = useState(null);

  // Charger immédiatement le distributeur depuis le cache, puis vérifier la position en arrière-plan
  useEffect(() => {
    const init = async () => {
      try {
        console.log("🚀 Initialisation du LocationContext...");
        
        // Charger le lieu de livraison sauvegardé
        const savedDeliveryLocation = getStorageItem('deliveryLocation');
        const savedDeliveryAddress = getStorageItem('deliveryAddress');
        
        if (savedDeliveryLocation && savedDeliveryAddress) {
          try {
            const parsedLocation = JSON.parse(savedDeliveryLocation);
            setDeliveryLocation(parsedLocation);
            setDeliveryAddress(savedDeliveryAddress);
            console.log("📍 Lieu de livraison chargé depuis le cache:", parsedLocation);
          } catch (parseError) {
            console.warn("Erreur lors du parsing du lieu de livraison:", parseError);
          }
        }
		
		const savedHistory = getStorageItem('deliveryHistory');
		if (savedHistory) {
		  try {
			const parsedHistory = JSON.parse(savedHistory);
			setDeliveryHistory(parsedHistory);
			console.log("📍 Historique des lieux chargé:", parsedHistory.length, "lieux");
		  } catch (parseError) {
			console.warn("Erreur lors du parsing de l'historique:", parseError);
		  }
		}
        
        // ÉTAPE 1: Charger immédiatement le distributeur depuis le cache
        const cachedDistributorId = getStorageItem('distributorId');
        const cachedDistributor = getStorageItem('nearestDistributor');
        
        if (cachedDistributorId && cachedDistributor) {
          try {
            const parsedDistributor = JSON.parse(cachedDistributor);
            console.log("✅ Distributeur chargé depuis le cache:", parsedDistributor);
            
            setDistributorId(parseInt(cachedDistributorId));
            setNearestDistributor(parsedDistributor);
            
            // LE DISTRIBUTEUR EST MAINTENANT DISPONIBLE IMMÉDIATEMENT
            setLoading(false);
            
            // ÉTAPE 2: Vérifier en arrière-plan si l'utilisateur a changé de position
            setTimeout(() => {
              verifyLocationInBackground();
            }, 100); // Petit délai pour laisser le composant se monter
            
            return; // Sortir ici, l'initialisation principale est terminée
          } catch (parseError) {
            console.warn("Erreur lors du parsing du distributeur en cache:", parseError);
          }
        }
        
        // ÉTAPE 1.5: Si pas de cache, essayer de récupérer un distributeur par défaut rapidement
        console.log("🔄 Aucun distributeur en cache, récupération du distributeur par défaut...");
        
        try {
          const defaultDistributor = await getDefaultDistributorQuickly();
          setNearestDistributor(defaultDistributor);
          setDistributorId(defaultDistributor.id);
          
          // Sauvegarder immédiatement dans le cache
          setStorageItem('distributorId', defaultDistributor.id.toString());
          setStorageItem('nearestDistributor', JSON.stringify(defaultDistributor));
          
          console.log("✅ Distributeur par défaut récupéré:", defaultDistributor);
          setLoading(false);
          
          // Ensuite essayer de trouver un meilleur distributeur en arrière-plan
          setTimeout(() => {
            findBestDistributorInBackground();
          }, 100);
          
        } catch (error) {
          console.error("❌ Erreur lors de la récupération du distributeur par défaut:", error);
          setError('Impossible de charger le distributeur');
          setLoading(false);
        }
        
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation de la localisation:', error);
        setError('Erreur lors de l\'initialisation de la localisation');
        setLoading(false);
      }
    };

    init();
  }, []);

  // Fonction pour obtenir rapidement un distributeur par défaut (sans appel API si possible)
  const getDefaultDistributorQuickly = async () => {
    // D'abord essayer le distributeur en dur (immédiat)
    const fallbackDistributor = {
      id: 1,
      nom: "JM Distribution SARL",
      adresse: "Bonamoussadi Sable",
      telephone: "671234567",
      email: "contact@distributeur.com",
      latitude: 4.08313,
      longitude: 9.72586,
      distance: 0,
      isDefault: true,
      loadedFrom: 'fallback'
    };

    // Si on est en ligne, essayer rapidement de récupérer depuis l'API
    if (navigator.onLine) {
      try {
        const apiDistributor = await Promise.race([
          ApiService.fetchDefaultDistributor(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 2000) // Timeout de 2 secondes
          )
        ]);
        
        console.log("✅ Distributeur récupéré depuis l'API rapidement");
        return { ...apiDistributor, loadedFrom: 'api' };
      } catch (error) {
        console.log("⏱️ API trop lente ou en erreur, utilisation du fallback");
      }
    }
    
    return fallbackDistributor;
  };

  // Vérification en arrière-plan si l'utilisateur a changé de position
  const verifyLocationInBackground = async () => {
    console.log("🔍 Vérification de la position en arrière-plan...");
    setBackgroundUpdate(true);
    
    try {
      // Vérifier si l'utilisateur a ignoré la localisation
      const skipped = getStorageItem('locationSkipped');
      if (skipped === 'true') {
        console.log("📍 Localisation ignorée par l'utilisateur");
        setLocationSkipped(true);
        setBackgroundUpdate(false);
        return;
      }

      // Récupérer la position sauvegardée
      const savedLocation = getStorageItem('userLocation');
      if (savedLocation) {
        try {
          const parsedLocation = JSON.parse(savedLocation);
          const locationAge = Date.now() - parsedLocation.timestamp;
          
          // Si la position est récente (moins de 1 heure), ne pas la revérifier
          if (locationAge < 3600000) { // 1 heure
            console.log("📍 Position récente, pas de révérification nécessaire");
            setUserLocation(parsedLocation);
            setBackgroundUpdate(false);
            return;
          }
          
          console.log("📍 Position ancienne, tentative de mise à jour...");
          setUserLocation(parsedLocation);
          
          // Essayer d'obtenir une position plus récente (sans bloquer l'UI)
          await tryUpdateLocationInBackground();
          
        } catch (parseError) {
          console.error('Erreur lors du parsing de la position:', parseError);
        }
      } else {
        console.log("📍 Aucune position sauvegardée");
      }
      
    } catch (error) {
      console.error('Erreur lors de la vérification de la position:', error);
    } finally {
      setBackgroundUpdate(false);
    }
  };

  // Tentative de mise à jour de la position en arrière-plan
  const tryUpdateLocationInBackground = async () => {
    if (!navigator.geolocation) {
      console.log("📍 Géolocalisation non supportée");
      return;
    }

    try {
      console.log("📍 Tentative d'obtention d'une nouvelle position...");
      
      const position = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Timeout de géolocalisation'));
        }, 10000); // 10 secondes max
        
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeoutId);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timeoutId);
            reject(err);
          },
          {
            enableHighAccuracy: false, // Plus rapide
            timeout: 8000,
            maximumAge: 300000 // Accepter une position de moins de 5 minutes
          }
        );
      });

      const newLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: Date.now()
      };

      console.log("📍 Nouvelle position obtenue:", newLocation);

      // Vérifier si l'utilisateur a bougé significativement
      const oldLocation = JSON.parse(getStorageItem('userLocation'));
      const distance = calculateDistance(
        oldLocation.latitude,
        oldLocation.longitude,
        newLocation.latitude,
        newLocation.longitude
      );

      console.log(`📍 Distance depuis la dernière position: ${distance.toFixed(2)} km`);

      // Si l'utilisateur a bougé de plus de 1 km, recalculer le distributeur
      if (distance > 1) {
        console.log("📍 Utilisateur a bougé, recalcul du distributeur...");
        setUserLocation(newLocation);
        setStorageItem('userLocation', JSON.stringify(newLocation));
        
        // Recalculer le distributeur le plus proche
        await findNearestDistributor(newLocation.latitude, newLocation.longitude, true);
      } else {
        console.log("📍 Utilisateur n'a pas bougé significativement");
        // Mettre à jour juste le timestamp
        setUserLocation(newLocation);
        setStorageItem('userLocation', JSON.stringify(newLocation));
      }

    } catch (error) {
      console.log("📍 Impossible d'obtenir une nouvelle position:", error.message);
      // Ce n'est pas grave, on continue avec l'ancienne position
    }
  };

  // Recherche du meilleur distributeur en arrière-plan (pour l'initialisation sans cache)
  const findBestDistributorInBackground = async () => {
    console.log("🔍 Recherche du meilleur distributeur en arrière-plan...");
    setBackgroundUpdate(true);
    
    try {
      // Récupérer la position de l'utilisateur
      const savedLocation = getStorageItem('userLocation');
      if (savedLocation) {
        const parsedLocation = JSON.parse(savedLocation);
        await findNearestDistributor(parsedLocation.latitude, parsedLocation.longitude, true);
      } else {
        console.log("📍 Aucune position disponible pour optimiser le distributeur");
      }
    } catch (error) {
      console.error('Erreur lors de la recherche du meilleur distributeur:', error);
    } finally {
      setBackgroundUpdate(false);
    }
  };

  // Trouver le distributeur le plus proche (version optimisée)
  const findNearestDistributor = async (latitude, longitude, isBackgroundUpdate = false) => {
    try {
      console.log(`🔍 Recherche du distributeur le plus proche pour: ${latitude}, ${longitude}`);
      
      if (!isBackgroundUpdate) {
        setLoading(true);
      }
      
      // Récupérer tous les distributeurs
      const distributeurs = await ApiService.fetchAllDistributeurs();
      
      if (!distributeurs || !Array.isArray(distributeurs) || distributeurs.length === 0) {
        throw new Error('Aucun distributeur trouvé');
      }
      
      // Calculer la distance pour chaque distributeur
      const distributorsWithDistance = distributeurs
        .map(distributor => {
          if (typeof distributor.latitude !== 'number' || typeof distributor.longitude !== 'number') {
            return { ...distributor, distance: Infinity };
          }
          
          const distance = calculateDistance(
            latitude,
            longitude,
            distributor.latitude,
            distributor.longitude
          );
          return { ...distributor, distance };
        })
        .filter(d => d.distance !== Infinity)
        .sort((a, b) => a.distance - b.distance);
      
      if (distributorsWithDistance.length === 0) {
        throw new Error('Aucun distributeur valide trouvé');
      }
      
      const nearest = distributorsWithDistance[0];
      console.log("✅ Distributeur le plus proche trouvé:", nearest);
      
      // Mettre à jour seulement si c'est un distributeur différent
      if (!nearestDistributor || nearest.id !== nearestDistributor.id) {
        console.log("🔄 Mise à jour du distributeur:", nearest.nom);
        
        // ✅ NOUVEAU : Sauvegarder l'ancien ID avant de changer
        const oldDistributorId = distributorId;
        
        setNearestDistributor(nearest);
        setDistributorId(nearest.id);
        
        // Sauvegarder dans le cache
        setStorageItem('distributorId', nearest.id.toString());
        setStorageItem('nearestDistributor', JSON.stringify(nearest));
        
        // ✅ NOUVEAU : Notifier le changement de distributeur
        if (oldDistributorId && distributorChangeCallback) {
          console.log("🛒 Notification du changement de distributeur");
          distributorChangeCallback(oldDistributorId, nearest.id);
        }
      } else {
        console.log("✅ Distributeur inchangé");
      }
      
      if (!isBackgroundUpdate) {
        setLoading(false);
      }
      
      return nearest;
    } catch (error) {
      console.error('Erreur lors de la recherche du distributeur:', error);
      
      if (!isBackgroundUpdate) {
        setError('Impossible de trouver un distributeur près de vous');
        setLoading(false);
      }
      
      // En cas d'erreur, essayer le cache
      const cached = getStorageItem('nearestDistributor');
      if (cached && !nearestDistributor) {
        try {
          const distributor = JSON.parse(cached);
          setNearestDistributor(distributor);
          setDistributorId(distributor.id);
          return distributor;
        } catch (parseError) {
          console.error("Erreur lors du parsing du distributeur en cache:", parseError);
        }
      }
      
      return null;
    }
  };

  // Activer la géolocalisation (fonction améliorée)
  const enableGeolocation = () => {
    return new Promise(async (resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMessage = 'La géolocalisation n\'est pas supportée par votre navigateur';
        setError(errorMessage);
        reject(new Error(errorMessage));
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 30000
          });
        });
        
        const userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: Date.now()
        };
        
        console.log("📍 Position obtenue:", userLocation);
        
        // Sauvegarder la position
        setUserLocation(userLocation);
        setStorageItem('userLocation', JSON.stringify(userLocation));
        
        // Trouver le distributeur le plus proche
        const distributor = await findNearestDistributor(userLocation.latitude, userLocation.longitude);
        setLoading(false);
        resolve(distributor);
        
      } catch (error) {
        console.error('Erreur de géolocalisation:', error);
        
        let errorMessage = 'Erreur lors de l\'obtention de votre position';
        
        if (error.code === 1) {
          errorMessage = 'Vous avez refusé l\'accès à votre position';
        } else if (error.code === 2) {
          errorMessage = 'Position non disponible. Vérifiez que le GPS est activé';
        } else if (error.code === 3) {
          errorMessage = 'La demande de position a expiré';
        }
        
        setError(errorMessage);
        setLoading(false);
        reject(new Error(errorMessage));
      }
    });
  };

  // Ignorer la localisation
  const skipGeolocation = async () => {
    try {
      setLocationSkipped(true);
      setStorageItem('locationSkipped', 'true');
      
      // Utiliser le distributeur déjà chargé ou un distributeur par défaut
      if (!nearestDistributor) {
        const defaultDistributor = await getDefaultDistributorQuickly();
        setNearestDistributor(defaultDistributor);
        setDistributorId(defaultDistributor.id);
        setStorageItem('distributorId', defaultDistributor.id.toString());
        setStorageItem('nearestDistributor', JSON.stringify(defaultDistributor));
      }
      
      return nearestDistributor;
    } catch (error) {
      console.error('Erreur lors de l\'ignorance de la géolocalisation:', error);
      setError('Impossible de charger le distributeur par défaut');
      return null;
    }
  };

	// Nouvelle fonction pour définir le lieu de livraison
	const setDeliveryLocationData = async (placeDetails, address) => {
	  try {
		console.log("📍 Définition du lieu de livraison:", placeDetails);
		
		const location = {
		  latitude: placeDetails.latitude,
		  longitude: placeDetails.longitude,
		  address: address,
		  name: placeDetails.name,
		  timestamp: Date.now()
		};
		
		setDeliveryLocation(location);
		setDeliveryAddress(address);
		
		// Sauvegarder dans le localStorage
		setStorageItem('deliveryLocation', JSON.stringify(location));
		setStorageItem('deliveryAddress', address);
		
		// NOUVEAU : Ajouter à l'historique
		addToDeliveryHistory(location, address);
		
		// Trouver le distributeur le plus proche de ce lieu
		const distributor = await findNearestDistributor(location.latitude, location.longitude);
		
		return distributor;
	  } catch (error) {
		console.error('Erreur lors de la définition du lieu de livraison:', error);
		throw error;
	  }
	};

  // Ajouter un lieu à l'historique
	const addToDeliveryHistory = (location, address) => {
	  try {
		const newHistoryItem = {
		  id: Date.now(), // ID unique basé sur le timestamp
		  latitude: location.latitude,
		  longitude: location.longitude,
		  address: address,
		  name: location.name || address.split(',')[0], // Nom ou première partie de l'adresse
		  timestamp: Date.now(),
		  useCount: 1 // Compteur d'utilisation pour trier par popularité
		};

		// Récupérer l'historique actuel
		let updatedHistory = [...deliveryHistory];
		
		// Vérifier si ce lieu existe déjà (par coordonnées ou adresse similaire)
		const existingIndex = updatedHistory.findIndex(item => 
		  (Math.abs(item.latitude - location.latitude) < 0.0001 && 
		   Math.abs(item.longitude - location.longitude) < 0.0001) ||
		  item.address.toLowerCase() === address.toLowerCase()
		);

		if (existingIndex !== -1) {
		  // Si le lieu existe, augmenter son compteur d'utilisation et le remonter
		  updatedHistory[existingIndex].useCount += 1;
		  updatedHistory[existingIndex].timestamp = Date.now();
		  
		  // Réorganiser pour mettre les plus utilisés en premier
		  updatedHistory.sort((a, b) => b.useCount - a.useCount);
		} else {
		  // Ajouter le nouveau lieu au début
		  updatedHistory.unshift(newHistoryItem);
		  
		  // Limiter l'historique à 5 lieux maximum
		  if (updatedHistory.length > 5) {
			updatedHistory = updatedHistory.slice(0, 5);
		  }
		}

		// Sauvegarder
		setDeliveryHistory(updatedHistory);
		setStorageItem('deliveryHistory', JSON.stringify(updatedHistory));
		
		console.log("📍 Lieu ajouté à l'historique:", newHistoryItem.name);
	  } catch (error) {
		console.error("Erreur lors de l'ajout à l'historique:", error);
	  }
	};

	// Supprimer un lieu de l'historique
	const removeFromDeliveryHistory = (locationId) => {
	  try {
		const updatedHistory = deliveryHistory.filter(item => item.id !== locationId);
		setDeliveryHistory(updatedHistory);
		setStorageItem('deliveryHistory', JSON.stringify(updatedHistory));
		
		console.log("🗑️ Lieu supprimé de l'historique");
	  } catch (error) {
		console.error("Erreur lors de la suppression de l'historique:", error);
	  }
	};

	// Sélectionner un lieu depuis l'historique
	const selectFromDeliveryHistory = async (historyItem) => {
	  try {
		console.log("📍 Sélection depuis l'historique:", historyItem);
		
		// Créer un objet location compatible
		const location = {
		  latitude: historyItem.latitude,
		  longitude: historyItem.longitude,
		  address: historyItem.address,
		  name: historyItem.name,
		  timestamp: Date.now()
		};
		
		// Mettre à jour le lieu de livraison actuel
		setDeliveryLocation(location);
		setDeliveryAddress(historyItem.address);
		
		// Sauvegarder dans le localStorage
		setStorageItem('deliveryLocation', JSON.stringify(location));
		setStorageItem('deliveryAddress', historyItem.address);
		
		// Augmenter le compteur d'utilisation
		addToDeliveryHistory(location, historyItem.address);
		
		// Trouver le distributeur le plus proche
		const distributor = await findNearestDistributor(location.latitude, location.longitude);
		
		return distributor;
	  } catch (error) {
		console.error('Erreur lors de la sélection depuis l\'historique:', error);
		throw error;
	  }
	};
	
  // Réinitialiser le lieu de livraison
  const clearDeliveryLocation = () => {
    setDeliveryLocation(null);
    setDeliveryAddress('');
    localStorage.removeItem('deliveryLocation');
    localStorage.removeItem('deliveryAddress');
  };

  // Calculer la distance entre deux points géographiques (formule de Haversine)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
        typeof lat2 !== 'number' || typeof lon2 !== 'number') {
      console.warn("Coordonnées invalides pour le calcul de distance:", {lat1, lon1, lat2, lon2});
      return Infinity;
    }
    
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance;
  };

  return (
    <LocationContext.Provider
      value={{
        userLocation,
        locationSkipped,
        distributorId,
        nearestDistributor,
        loading,
        error,
        backgroundUpdate,
        deliveryLocation,
        deliveryAddress,
		deliveryHistory, // NOUVEAU
        enableGeolocation,
        skipGeolocation,
        findNearestDistributor,
        calculateDistance,
        setDeliveryLocationData,
        clearDeliveryLocation,
		addToDeliveryHistory, // NOUVEAU
		removeFromDeliveryHistory, // NOUVEAU
		selectFromDeliveryHistory, // NOUVEAU
		setDistributorChangeCallback // ✅ NOUVEAU : Ajout du setter dans le contexte
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export default LocationProvider;